import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { ModulesState } from 'src/app/modules/state/modules.state';
import { Observable, Subject, merge } from 'rxjs';
import { GetModulePrimaryCategories, GetModuleSecondaryCategories } from 'src/app/modules/state/modules.actions';
import { FormBuilder, FormGroup, AbstractControl } from '@angular/forms';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { GalleryState } from '../../state/gallery.state';
import { GallerySearchMode } from '../../models/gallery-search-mode.enum';
import { GalleryActions } from '../../state/gallery.actions';

interface SearchQuery {
  primaryCategory: string;
  secondaryCategory: string;
  name: string;
}

@Component({
  selector: 'app-gallery-filters-loft',
  templateUrl: './gallery-filters-loft.component.html',
  styleUrls: ['./gallery-filters-loft.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryFiltersLoftComponent implements OnInit, OnDestroy {
  gallerySearchMode = GallerySearchMode;

  @Select(ModulesState.getPrimaryCategoryOptions)
  primaryCategoryOptions$: Observable<string[]>;

  @Select(ModulesState.getSecondaryCategoryOptions)
  secondaryCategoryOptions$: Observable<string[]>;

  @Select(GalleryState.getSearchMode)
  searchMode$: Observable<GallerySearchMode>;

  get primaryCategoryControl(): AbstractControl {
    return this.queryForm.get('primaryCategory');
  }

  get secondaryCategoryControl(): AbstractControl {
    return this.queryForm.get('secondaryCategory');
  }

  get pathControl(): AbstractControl {
    return this.pathForm.get('path');
  }

  queryForm: FormGroup;
  pathForm: FormGroup;
  ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private formBuilder: FormBuilder
  ) { }

  ngOnInit() {
    this.initForms();
    this.initFormListeners();

    this.store.dispatch(new GetModulePrimaryCategories());
    this.store.dispatch(new GetModuleSecondaryCategories(this.primaryCategoryControl.value));
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initForms() {
    this.queryForm = this.formBuilder.group({
      'primaryCategory': ['production'],
      'secondaryCategory': ['maleAdult']
    });

    this.pathForm = this.formBuilder.group({
      'path': []
    });

    this.searchLoft(this.getQuery());
  }

  initFormListeners() {
    // Update the secondary category, since it's dependent on the primary to populate
    this.primaryCategoryControl.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value) => {
        this.handlePrimaryUpdate(value);
      });

    // Handle all changes
    merge(this.queryForm.valueChanges, this.pathForm.valueChanges)
      .pipe(
        debounceTime(300),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.searchLoft(this.getQuery());
      });
      
  }

  toggleSearchMode(mode: GallerySearchMode) {
    this.store.dispatch(new GalleryActions.SetSearchMode(mode));
  }

  handlePrimaryUpdate(value) {
    this.secondaryCategoryControl.setValue('');
    this.store.dispatch(new GetModuleSecondaryCategories(value));
  }

  searchLoft(query: SearchQuery) {
    this.store.dispatch(new GalleryActions.SearchLoft(query.primaryCategory, query.secondaryCategory, query.name));
  }

  getQuery(): SearchQuery {
    const result: SearchQuery = {
      primaryCategory: '',
      secondaryCategory: '',
      name: ''
    };

    switch (this.store.selectSnapshot(GalleryState.getSearchMode)) {
      case GallerySearchMode.Query:
        result.primaryCategory = this.primaryCategoryControl.value;
        result.secondaryCategory = this.secondaryCategoryControl.value;
        break;
      
      case GallerySearchMode.Path:
        const query = decodeURIComponent(<string>this.pathControl.value || '');
        const paths = query.split('/');

        // Match directories by checking for trailing /
        const regex = /([A-Za-z_-\s0-9]*\/)/gi;
        const directories = query.match(regex);
        
        // Don't search when no primary directory is specified
        if (directories != null) {
          result.primaryCategory = (directories[0] || '').replace('/', '');
          result.secondaryCategory = (directories[1] || '').replace('/', '');
          result.name = paths.pop();
        }
    }

    return result;
  }


}
